/**
 * Branch Detail — drill-in for one branch (PHASE2_SPEC §4.9).
 *
 * Route: `/banks/:orgId/branches/:branchIdLocal`
 * Layout:
 *   Breadcrumb · Header (branch + bank · "View bank →")
 *   8 KPI tiles
 *   Two donut charts (deposit mix · NPA mix)
 *   Top loan + deposit products
 *   Sibling branches list
 *   DQ chip strip
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Card from '../../components/Card';
import Donut, { DonutLegend, type DonutDatum } from '../../components/Donut';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { dqFlag, npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { DEPOSIT_COLORS, NPA_COLORS } from '../../helpers/palette';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchBranchDetail, selectBranchDetail } from '../../redux/slices/dashboard';
import type {
	BankDetailTopProductDeposit,
	BankDetailTopProductLoan,
	BranchDetailSibling,
} from '../../type/api';

export default function BranchDetailPage() {
	const params = useParams<{ orgId: string; branchIdLocal: string }>();
	const orgId = Number(params.orgId);
	const branchIdLocal = params.branchIdLocal || '';
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectBranchDetail(orgId, branchIdLocal));

	useEffect(() => {
		if (!Number.isFinite(orgId) || !branchIdLocal) return;
		if (cell.status === 'idle') {
			dispatch(fetchBranchDetail({ orgId, branchIdLocal }));
		}
	}, [orgId, branchIdLocal, cell.status, dispatch]);

	const data = cell.data;

	const loanProducts: BankDetailTopProductLoan[] = useMemo(
		() => (Array.isArray(data?.topProducts?.loan) ? data!.topProducts.loan : []),
		[data],
	);
	const depositProducts: BankDetailTopProductDeposit[] = useMemo(
		() => (Array.isArray(data?.topProducts?.deposit) ? data!.topProducts.deposit : []),
		[data],
	);
	const siblings: BranchDetailSibling[] = useMemo(
		() => (Array.isArray(data?.siblingBranches) ? data!.siblingBranches : []),
		[data],
	);

	const handleRetry = () => dispatch(fetchBranchDetail({ orgId, branchIdLocal }));

	if (!Number.isFinite(orgId) || !branchIdLocal) {
		return (
			<div className="page-body">
				<Card><EmptyState title="Invalid branch path" /></Card>
			</div>
		);
	}
	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Branch</h1></div></div>
				<Card><LoadingSkeleton rows={6} columns={6} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Branch</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load branch detail'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Branch</h1></div></div>
				<Card><EmptyState title="No data for this branch" /></Card>
			</div>
		);
	}

	const branch = data.branch;
	const k = data.kpis;
	const dq = data.dq;

	const depositMixData: DonutDatum[] = [
		{ name: 'Savings', value: data.depositMix.savingsCr, color: DEPOSIT_COLORS.savings },
		{ name: 'FD', value: data.depositMix.fdCr, color: DEPOSIT_COLORS.fd },
		{ name: 'RD', value: data.depositMix.rdCr, color: DEPOSIT_COLORS.rd },
		{ name: 'Current', value: data.depositMix.currentCr, color: DEPOSIT_COLORS.current },
	];
	const npaMixData: DonutDatum[] = [
		{ name: 'Standard', value: data.npaMix.standardCr, color: NPA_COLORS.standard },
		{ name: 'Sub-Standard', value: data.npaMix.subStandardCr, color: NPA_COLORS.subStandard },
		{ name: 'Doubtful', value: data.npaMix.doubtfulCr, color: NPA_COLORS.doubtful },
		{ name: 'Loss', value: data.npaMix.lossCr, color: NPA_COLORS.loss },
	];

	return (
		<div className="page-body">
			<div className="breadcrumb">
				<Link to="/">Overview</Link>
				<span className="sep">/</span>
				<Link to={`/banks/${orgId}`}>{branch.orgName}</Link>
				<span className="sep">/</span>
				<span>{branch.branchName}</span>
			</div>
			<div className="page-head">
				<div>
					<h1 className="page-title">{branch.branchName}</h1>
					<div className="page-sub">
						Branch ID <b style={{ color: 'var(--text)' }}>{branch.branchIdLocal}</b> ·{' '}
						{branch.orgName} (#{branch.orgId})
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={() => navigate(`/banks/${orgId}`)}>
					View bank →
				</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Loan ₹Cr" value={formatCrore(k.loanCr, 2)} sub={`${formatCount(k.loanCount)} accts`} />
				<KpiTile label="Deposit ₹Cr" value={formatCrore(k.depositCr, 2)} sub={`${formatCount(k.depositCount)} accts`} />
				<KpiTile label="NPA ₹Cr" value={formatCrore(k.npaCr, 2)} />
				<KpiTile label="NPA %" value={formatPct(k.npaPct)} flag={k.npaFlag || npaFlag(k.npaPct)} flagVariant="npa" primary />
			</div>

			<div className="grid cols-4">
				<KpiTile label="KYC Accounts" value={formatCount(k.kycAccounts)} />
				<KpiTile label="Unique Customers" value={formatCount(k.uniqueCustomers)} />
				<KpiTile label="First Seen" value={branch.firstSeenDate ? formatDate(branch.firstSeenDate) : '—'} />
				<KpiTile label="Status" value={branch.isActive ? 'Active' : 'Inactive'} />
			</div>

			<div className="grid cols-2">
				<Card title="Deposit mix">
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'center' }}>
						<Donut data={depositMixData} />
						<DonutLegend data={depositMixData} />
					</div>
				</Card>
				<Card title="NPA mix">
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'center' }}>
						<Donut data={npaMixData} />
						<DonutLegend data={npaMixData} />
					</div>
				</Card>
			</div>

			<div className="grid cols-2">
				<Card title={`Top loan products (${loanProducts.length})`}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<th>Code</th>
								<th>Product</th>
								<th className="num">Loan ₹Cr</th>
								<th className="num">Accts</th>
								<th className="num">NPA %</th>
							</tr>
						</thead>
						<tbody>
							{loanProducts.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() => navigate(`/products/${encodeURIComponent(p.productCode)}?type=LOAN`)}
									tabIndex={0}
								>
									<td style={{ fontWeight: 600 }}>{p.productCode}</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.loanCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(p.loanCount)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={npaFlag(p.npaPct)} variant="npa" compact label={formatPct(p.npaPct)} />
									</td>
								</tr>
							))}
							{!loanProducts.length && (
								<tr><td colSpan={5}><EmptyState title="No loan products" /></td></tr>
							)}
						</tbody>
					</table>
				</Card>
				<Card title={`Top deposit products (${depositProducts.length})`}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<th>Code</th>
								<th>Product</th>
								<th className="num">Balance ₹Cr</th>
								<th className="num">Accts</th>
							</tr>
						</thead>
						<tbody>
							{depositProducts.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() => navigate(`/products/${encodeURIComponent(p.productCode)}?type=DEPOSIT`)}
									tabIndex={0}
								>
									<td style={{ fontWeight: 600 }}>{p.productCode}</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.balanceCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(p.count)}</td>
								</tr>
							))}
							{!depositProducts.length && (
								<tr><td colSpan={4}><EmptyState title="No deposit products" /></td></tr>
							)}
						</tbody>
					</table>
				</Card>
			</div>

			<Card title={`Sibling branches (${siblings.length})`}>
				<table className="tbl" style={{ width: '100%' }}>
					<thead>
						<tr>
							<th>Branch ID</th>
							<th>Branch</th>
							<th className="num">NPA %</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{siblings.map((s) => (
							<tr
								key={s.branchIdLocal}
								className="clickable"
								onClick={() => navigate(`/banks/${orgId}/branches/${encodeURIComponent(s.branchIdLocal)}`)}
								tabIndex={0}
							>
								<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{s.branchIdLocal}</td>
								<td>{s.branchName}</td>
								<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPct(s.npaPct)}</td>
								<td><StatusPill flag={s.npaFlag} variant="npa" /></td>
							</tr>
						))}
						{!siblings.length && (
							<tr><td colSpan={4}><EmptyState title="No sibling branches" /></td></tr>
						)}
					</tbody>
				</table>
			</Card>

			<Card title="Data quality">
				<div className="dq-strip">
					<span className={`chip ${dqFlag(dq.missingPanPct).toLowerCase() === 'green' ? 'good' : dqFlag(dq.missingPanPct).toLowerCase() === 'red' ? 'bad' : 'warn'}`}>
						PAN missing: {formatPct(dq.missingPanPct)} ({formatCount(dq.missingPanCount)})
					</span>
					<span className={`chip ${dqFlag(dq.missingMobilePct).toLowerCase() === 'green' ? 'good' : dqFlag(dq.missingMobilePct).toLowerCase() === 'red' ? 'bad' : 'warn'}`}>
						Mobile missing: {formatPct(dq.missingMobilePct)} ({formatCount(dq.missingMobileCount)})
					</span>
					<span className={`chip ${dqFlag(dq.missingNomineePct).toLowerCase() === 'green' ? 'good' : dqFlag(dq.missingNomineePct).toLowerCase() === 'red' ? 'bad' : 'warn'}`}>
						Nominee missing: {formatPct(dq.missingNomineePct)} ({formatCount(dq.missingNomineeCount)})
					</span>
				</div>
			</Card>
		</div>
	);
}
